import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { cancelEmployeeInviteApi, inviteEmployeeApi } from '../services/api';
import {
  cancelInvitation,
  listInvitations,
  resendInvitation,
  sendInvitation,
  type InvitePlatformRole,
} from '../services/auth';
import { useAppStore } from '../store/useAppStore';
import type { Employee } from '../types';
import Button from './Button';
import Icon from './Icon';
import Modal from './Modal';

/**
 * "Invite to sign in" from an employee's profile.
 *
 * Two halves, in this order: People records which record the address is for,
 * then the platform sends the invitation (products: ['people']). When the
 * invite is accepted, People links that login to exactly this record — on the
 * platform's entitlement.granted event, or on the person's first sign-in.
 *
 * If the platform send fails, the People half is rolled back, so a record never
 * shows "invite pending" for an invitation nobody received.
 *
 * HR only. Renders nothing once the record has a login.
 */

const ROLE_OPTIONS: Array<{ value: InvitePlatformRole; label: string; hint: string }> = [
  { value: 'user', label: 'Employee', hint: 'Their own leave, attendance and payslips' },
  { value: 'admin', label: 'Manager', hint: 'Plus their team’s approvals' },
  { value: 'org_admin', label: 'HR admin', hint: 'Full HR access' },
];

interface Props {
  employee: Employee;
  canEdit: boolean;
}

export default function SignInAccessPanel({ employee, canEdit }: Props) {
  const { orgId, token, toast } = useAppStore();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ email: string; role: InvitePlatformRole }>({ email: '', role: 'user' });

  const pendingEmail = employee.invitedEmail;
  const { data: pendingInvites = [] } = useQuery({
    queryKey: qk.pendingInvitations(orgId),
    queryFn: () => listInvitations(orgId, token ?? ''),
    enabled: canEdit && !!pendingEmail && !!orgId && !!token,
    retry: false,
  });
  const platformInvite = pendingEmail
    ? pendingInvites.find((i) => i.email.toLowerCase() === pendingEmail.toLowerCase())
    : undefined;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: qk.employee(employee.id) });
    queryClient.invalidateQueries({ queryKey: qk.employees() });
    queryClient.invalidateQueries({ queryKey: qk.pendingInvitations(orgId) });
  };

  const inviteMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Organisation not found — sign out and back in.');
      const email = form.email.trim().toLowerCase();
      const bound = await inviteEmployeeApi(employee.id, email);
      try {
        await sendInvitation(orgId, bound.invitedEmail ?? email, form.role, token ?? '');
      } catch (err) {
        await cancelEmployeeInviteApi(employee.id).catch(() => undefined);
        throw err;
      }
      return bound.invitedEmail ?? email;
    },
    onSuccess: (email) => {
      toast(`Invitation sent to ${email}`);
      setOpen(false);
      refresh();
    },
    onError: (err: unknown) => toast(errorMessage(err, 'Could not send the invitation'), 'danger'),
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (platformInvite) return resendInvitation(orgId, platformInvite.id, token ?? '');
      // The platform invite expired or was removed: send a fresh one to the same address.
      return sendInvitation(orgId, pendingEmail!, 'user', token ?? '');
    },
    onSuccess: () => {
      toast('Invitation resent');
      refresh();
    },
    onError: (err: unknown) => toast(errorMessage(err, 'Could not resend the invitation'), 'danger'),
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (platformInvite) await cancelInvitation(orgId, platformInvite.id, token ?? '');
      await cancelEmployeeInviteApi(employee.id);
    },
    onSuccess: () => {
      toast('Invitation cancelled');
      refresh();
    },
    onError: (err: unknown) => toast(errorMessage(err, 'Could not cancel the invitation'), 'danger'),
  });

  if (!canEdit || employee.platformUserId || employee.status === 'separated') return null;

  const openModal = () => {
    setForm({ email: employee.email ?? '', role: 'user' });
    setOpen(true);
  };
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  return (
    <div className="t-card" style={{ marginBottom: 20 }}>
      <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        {pendingEmail ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon name="mail" size={16} color="var(--warn, #d97706)" />
              <div>
                <p style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>Invitation pending</p>
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                  Sent to {pendingEmail}
                  {employee.invitedAt ? ` on ${new Date(employee.invitedAt).toLocaleDateString()}` : ''}.
                  {' '}They’re linked to this record as soon as they accept.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" size="sm" disabled={resendMutation.isPending} onClick={() => resendMutation.mutate()}>
                {resendMutation.isPending ? 'Resending…' : 'Resend'}
              </Button>
              <Button variant="ghost" size="sm" disabled={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                Cancel invite
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon name="user" size={16} color="var(--ink-3)" />
              <div>
                <p style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>Not signed in yet</p>
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                  Invite them to SK People to see their own leave, attendance and payslips.
                </p>
              </div>
            </div>
            <Button variant="accent" size="sm" onClick={openModal}>Invite to sign in</Button>
          </>
        )}
      </div>

      {open && (
        <Modal
          title={`Invite ${employee.firstName || 'employee'} to sign in`}
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                variant="accent"
                disabled={inviteMutation.isPending || !emailValid}
                onClick={() => inviteMutation.mutate()}
              >
                {inviteMutation.isPending ? 'Sending…' : 'Send invitation'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
              They get an email, set their own password, and land on this employee record.
            </p>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Email</span>
              <input
                type="email"
                className="t-input"
                placeholder="name@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && emailValid && inviteMutation.mutate()}
              />
            </label>
            <div>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Access</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="invite-role"
                      checked={form.role === opt.value}
                      onChange={() => setForm({ ...form, role: opt.value })}
                    />
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>{opt.label}</span>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{opt.hint}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** The API's own message where there is one (e.g. "already has a pending invite"). */
function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string }; title?: string } }; message?: string };
  return e?.response?.data?.error?.message ?? e?.response?.data?.title ?? fallback;
}
